import { defineComponent as _defineComponent } from 'vue'
import TUIChatEngine, {
  TUIStore,
  StoreName,
  TUITranslateService,
  IGroupModel,
  Friend,
  FriendApplication,
} from "@tencentcloud/chat-uikit-engine";
import { TUIGlobal } from "@tencentcloud/universal-api";
import { ref, computed, onMounted, onUnmounted } from "../../../adapter-vue";
import { isPC } from "../../../utils/env";

import {
  generateAvatar,
  generateContactInfoName,
  generateContactInfoBasic,
  isFriend,
  isApplicationType,
} from "../utils/index";
import {
  contactMoreInfoConfig,
  contactButtonConfig,
} from "./contact-info-config";
import Icon from "../../common/Icon.vue";
import editSVG from "../../../assets/icon/edit.svg";
import backSVG from "../../../assets/icon/back.svg";
import SwitchBar from "../../common/SwitchBar/index.vue";
import {
  IBlackListUserItem,
  IContactInfoMoreItem,
  IContactInfoButton,
} from "../../../interface";
import {
  CONTACT_INFO_LABEL_POSITION,
  CONTACT_INFO_MORE_EDIT_TYPE,
  CONTACT_INFO_BUTTON_TYPE,
} from "../../../constant";
import { deepCopy } from "../../TUIChat/utils/utils";
import { slice } from "lodash";

type IContactInfoType =
  | IGroupModel
  | Friend
  | FriendApplication
  | IBlackListUserItem;


export default /*#__PURE__*/_defineComponent({
  __name: 'index',
  emits: ["switchConversation"],
  setup(__props, { emit: emits }) {



const contactInfoData = ref<IContactInfoType>({} as IContactInfoType);
const contactInfoBasicList = ref<Array<{ label: string; data: string }>>([]);
const contactInfoMoreList = ref<IContactInfoMoreItem[]>([]);
const contactInfoButtonList = ref<IContactInfoButton[]>([]);

const setEditing = (item: any) => {
  item.editing = true;
};

const isGroup = computed((): boolean =>
  (contactInfoData.value as IGroupModel)?.groupID ? true : false
);

const isApplication = computed((): boolean => {
  return isApplicationType(contactInfoData?.value);
});

// is both friend, if is group type always false
const isBothFriend = ref<boolean>(false);

// is group member, including ordinary member, admin, group owner
const isGroupMember = computed((): boolean => {
  return (contactInfoData.value as IGroupModel)?.selfInfo?.userID
    ? true
    : false;
});

// is in black list, if is group type always false
const isInBlackList = computed((): boolean => {
  return (
    !isGroup.value &&
    blackList.value?.findIndex(
      (item: IBlackListUserItem) =>
        item?.userID === (contactInfoData.value as IBlackListUserItem)?.userID
    ) >= 0
  );
});

const blackList = ref<IBlackListUserItem[]>([]);

onMounted(() => {
  TUIStore.watch(StoreName.CUSTOM, {
    currentContactInfo: onCurrentContactInfoUpdated,
  });
  TUIStore.watch(StoreName.USER, {
    userBlacklist: onUserBlacklistUpdated,
  });
});

onUnmounted(() => {
  TUIStore.unwatch(StoreName.CUSTOM, {
    currentContactInfo: onCurrentContactInfoUpdated,
  });
  TUIStore.unwatch(StoreName.USER, {
    userBlacklist: onUserBlacklistUpdated,
  });
});

const resetContactInfoUIData = () => {
  contactInfoData.value = {} as IContactInfoType;
  contactInfoBasicList.value = [];
  contactInfoMoreList.value = [];
  contactInfoButtonList.value = [];
};

const resetContactSearchingUIData = () => {
  TUIStore.update(StoreName.CUSTOM, "currentContactInfo", {});
  TUIStore.update(StoreName.CUSTOM, "currentContactSearchingStatus", false);
  TUIGlobal?.closeSearching && TUIGlobal?.closeSearching();
};

const onContactInfoEmitSubmit = (item: any) => {
  item.editSubmitHandler &&
    item.editSubmitHandler({
      item,
      contactInfoData: contactInfoData.value,
      isBothFriend: isBothFriend.value,
      isInBlackList: isInBlackList.value,
    });
};

const onContactInfoButtonClicked = (item: any) => {
  item.onClick &&
    item.onClick({
      contactInfoData: contactInfoData.value,
      contactInfoMoreList: contactInfoMoreList.value,
    });
  if (
    item.key === "enterGroupConversation" ||
    item.key === "enterC2CConversation"
  ) {
    emits("switchConversation", contactInfoData.value);
    resetContactSearchingUIData();
  }
};

const generateMoreInfo = async () => {
  if (!isApplication.value) {
    if (
      (!isGroup.value && !isBothFriend.value && !isInBlackList.value) ||
      (isGroup.value &&
        !isGroupMember.value &&
        (contactInfoData.value as IGroupModel)?.type !==
          TUIChatEngine?.TYPES?.GRP_AVCHATROOM)
    ) {
      contactMoreInfoConfig.setWords.data = "";
      contactInfoMoreList.value.push(contactMoreInfoConfig.setWords);
    }
    if (!isGroup.value && !isInBlackList.value) {
      contactMoreInfoConfig.setRemark.data =
        (contactInfoData.value as Friend)?.remark || "";
      contactMoreInfoConfig.setRemark.editing = false;
      contactInfoMoreList.value.push(contactMoreInfoConfig.setRemark);
    }
    if (!isGroup.value && (isBothFriend.value || isInBlackList.value)) {
      contactMoreInfoConfig.blackList.data = isInBlackList.value || false;
      contactInfoMoreList.value.push(contactMoreInfoConfig.blackList);
    }
  } else {
    contactMoreInfoConfig.displayWords.data =
      (contactInfoData.value as FriendApplication)?.wording || "";
    contactInfoMoreList.value.push(contactMoreInfoConfig.displayWords);
  }
};

const generateButton = () => {
  if (isInBlackList.value) {
    return;
  }
  if (isApplication.value) {
    if (
      (contactInfoData.value as FriendApplication)?.type ===
      TUIChatEngine?.TYPES?.SNS_APPLICATION_SENT_TO_ME
    ) {
      contactInfoButtonList?.value?.push(
        contactButtonConfig.refuseFriendApplication
      );
      contactInfoButtonList?.value?.push(
        contactButtonConfig.acceptFriendApplication
      );
    }
  } else {
    if (isGroup.value && isGroupMember.value) {
      switch ((contactInfoData.value as IGroupModel)?.selfInfo?.role) {
        case "Owner":
          contactInfoButtonList?.value?.push(contactButtonConfig.dismissGroup);
          break;
        default:
          // contactInfoButtonList?.value?.push(contactButtonConfig.quitGroup);
          break;
      }
      contactInfoButtonList?.value?.push(
        contactButtonConfig.enterGroupConversation
      );
    } else if (!isGroup.value && isBothFriend.value) {
      contactInfoButtonList?.value?.push(contactButtonConfig.deleteFriend);
      contactInfoButtonList?.value?.push(
        contactButtonConfig.enterC2CConversation
      );
    } else {
      if (isGroup.value) {
        contactInfoButtonList?.value?.push(
          (contactInfoData.value as IGroupModel)?.type ===
            TUIChatEngine?.TYPES?.GRP_AVCHATROOM
            ? contactButtonConfig.joinAVChatGroup
            : contactButtonConfig.joinGroup
        );
      } else {
        contactInfoButtonList?.value?.push(contactButtonConfig.addFriend);
      }
    }
  }
};

function onUserBlacklistUpdated(userBlacklist: IBlackListUserItem[]) {
  blackList.value = userBlacklist;
}

async function onCurrentContactInfoUpdated(contactInfo: IContactInfoType) {
  if (
    contactInfoData.value &&
    contactInfo &&
    JSON.stringify(contactInfoData.value) === JSON.stringify(contactInfo)
  ) {
    return;
  }
  resetContactInfoUIData();
  // deep clone
  contactInfoData.value = deepCopy(contactInfo) || {};
  if (
    !contactInfoData.value ||
    Object.keys(contactInfoData.value)?.length === 0
  ) {
    return;
  }
  contactInfoBasicList.value = generateContactInfoBasic(contactInfoData.value);
  isBothFriend.value = await isFriend(contactInfoData.value);
  generateMoreInfo();
  generateButton();
  if (contactInfo.infoKeyList) {
    contactInfoMoreList.value = contactInfo.infoKeyList.map((key: string) => {
      return (contactMoreInfoConfig as any)[key];
    });
  }
  if (contactInfo.btnKeyList) {
    contactInfoButtonList.value = contactInfo.btnKeyList.map((key: string) => {
      return (contactButtonConfig as any)[key];
    });
  }
}

return { __sfc: true,emits, contactInfoData, contactInfoBasicList, contactInfoMoreList, contactInfoButtonList, setEditing, isGroup, isApplication, isBothFriend, isGroupMember, isInBlackList, blackList, resetContactInfoUIData, resetContactSearchingUIData, onContactInfoEmitSubmit, onContactInfoButtonClicked, generateMoreInfo, generateButton, onUserBlacklistUpdated, onCurrentContactInfoUpdated, TUITranslateService, isPC, generateAvatar, generateContactInfoName, Icon, editSVG, backSVG, SwitchBar, CONTACT_INFO_LABEL_POSITION, CONTACT_INFO_MORE_EDIT_TYPE, CONTACT_INFO_BUTTON_TYPE }
}

})